/* eslint-disable i18next/no-literal-string */
import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Checkbox, RadioButton} from '@dropbox/dig-components/controls';
import {Popover} from '@dropbox/dig-components/popover';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Select, TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, DeleteLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import classNames from 'classnames';
import {AdminWireframeService} from 'client';
import {WorkstreamHeadcountDataRead} from 'client/models/WorkstreamHeadcountDataRead';
import {WorkstreamRow} from 'client/models/WorkstreamRow';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {
  isMultiCellClipboard,
  normalizePastedNumber,
  parseSpreadsheetClipboard,
} from 'helpers/spreadsheetClipboard';
import {
  ALLOCATION_FIELDS,
  EMPTY_STAFFING_VALUES,
  fillBlankStaffingValues,
  formatStaffingCents,
  GEO_FIELDS,
  isBlankStaffingValue,
  isSameStaffingValue,
  STAFFING_FIELDS,
  StaffingField,
  StaffingValidation,
  StaffingValues,
  toStaffingValues,
  validateStaffing,
} from 'helpers/staffingValues';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useSetAtom} from 'jotai';
import {ClipboardEvent, useId, useState} from 'react';
import {getService} from 'utilities';
import {useTimeframes} from 'views/workstreams/detail/hooks';
import {getDefaultWorkstreamTimeframe} from 'views/workstreams/detail/workstreamTimeframe';
import {wireframeReportQueryKey} from 'views/workstreams/page/wireframes/wireframeReportQuery';

import styles from './AdminWireframeData.module.css';
import {
  buildWireframePillarHierarchy,
  buildWireframeWorkstreamHierarchy,
  listWireframePillars,
} from './wireframeWorkstreamHierarchy';

const WORKSTREAM_COLUMN_WIDTH = 420;
const INPUT_COLUMN_WIDTH = 88;
const PROFILE_COLUMN_WIDTH = 180;
const ESTIMATED_COST_COLUMN_WIDTH = 140;
const CLEAR_COLUMN_WIDTH = 52;
const VALUE_FORMAT_ERROR = 'Enter a number with up to 2 decimal places';
const ALLOCATION_MISMATCH_ERROR = 'Sum of “Growth”, “CI”, and “KTLO” needs to match HC';
const HEADCOUNT_TOO_LARGE_ERROR = 'Sum of “Low”, “Mid”, and “High” must be less than 100,000,000';
// Editable table columns in display order, so pasted spreadsheet cells fill left to right.
const EDITABLE_COLUMNS: {field: StaffingField; label: string}[] = [
  {field: 'allocation_growth_value', label: 'Growth allocation'},
  {field: 'allocation_ci_value', label: 'CI allocation'},
  {field: 'allocation_ktlo_value', label: 'KTLO allocation'},
  {field: 'geo_low_value', label: 'Low geo distribution'},
  {field: 'geo_mid_value', label: 'Mid geo distribution'},
  {field: 'geo_high_value', label: 'High geo distribution'},
];
type Draft = {name: string; low: string; mid: string; high: string};
const emptyDraft: Draft = {name: '', low: '', mid: '', high: ''};
const toDraft = (profile: {
  name: string;
  annual_low_usd: number;
  annual_mid_usd: number;
  annual_high_usd: number;
}): Draft => ({
  name: profile.name,
  low: String(profile.annual_low_usd),
  mid: String(profile.annual_mid_usd),
  high: String(profile.annual_high_usd),
});

type WireframeTableRow = {
  workstream: WorkstreamRow;
  pillarId: number;
  depth: number;
  headcount: WorkstreamHeadcountDataRead | null;
  profileName: string | null;
  estimatedCost: number | null;
};

// Headcount is entered per workstream within a pillar.
const toHeadcountKey = (pillarId: number, workstreamId: number) => `${pillarId}:${workstreamId}`;
const fromHeadcountKey = (key: string) => {
  const [pillarId, workstreamId] = key.split(':').map(Number);
  return {pillar_workstream_id: pillarId, workstream_id: workstreamId};
};

// A row with every input blank has no headcount; saving it clears the stored row.
const isBlankStaffing = (values: StaffingValues) =>
  STAFFING_FIELDS.every((field) => isBlankStaffingValue(values[field]));

const effectiveGeoValue = (
  headcount: WorkstreamHeadcountDataRead | null,
  geo: 'low' | 'mid' | 'high'
) => {
  const value = Number(toStaffingValues(headcount)[`geo_${geo}_value`]);
  return Number.isFinite(value) ? value : 0;
};

export const AdminWireframeData = () => {
  useDocumentTitle('Wireframe Data');
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const timeframes = useTimeframes();
  // Empty until a timeframe is picked; the default is known once the timeframes load.
  const [pickedTimeframe, setPickedTimeframe] = useState('');
  const timeframe = pickedTimeframe || getDefaultWorkstreamTimeframe(new Date(), timeframes);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [newDraft, setNewDraft] = useState(emptyDraft);
  const [selectedWorkstreams, setSelectedWorkstreams] = useState<number[]>([]);
  const [headcountDrafts, setHeadcountDrafts] = useState<Record<string, StaffingValues>>({});
  const [selectedPillarId, setSelectedPillarId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [workstreamPickerOpen, setWorkstreamPickerOpen] = useState(false);
  const dataQuery = useQuery({
    queryKey: ['admin-wireframe-data', timeframe],
    queryFn: () =>
      getService(AdminWireframeService).getWireframeDataApiV1AdminWireframeDataGet(timeframe),
    enabled: Boolean(timeframe),
  });
  const isLoadingData = dataQuery.isLoading || !timeframe;
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: ['admin-wireframe-data', timeframe]}),
      // The /workstreams Wireframes report is computed from these inputs.
      queryClient.invalidateQueries({queryKey: wireframeReportQueryKey(timeframe)}),
    ]);
    setSnackbar({text: 'Changes saved.'});
  };
  const createMutation = useMutation({
    mutationFn: () =>
      getService(AdminWireframeService).createCostProfileApiV1AdminWireframeCostProfilesPost(
        {
          name: newDraft.name.trim(),
          annual_low_usd: Number(newDraft.low),
          annual_mid_usd: Number(newDraft.mid),
          annual_high_usd: Number(newDraft.high),
        },
        timeframe
      ),
    onSuccess: async (profile) => {
      await refresh();
      setNewDraft(emptyDraft);
      setSelectedProfileId(profile.id);
      setSelectedWorkstreams([]);
      setDraft(toDraft(profile));
    },
  });
  const saveMutation = useMutation({
    mutationFn: () =>
      getService(
        AdminWireframeService
      ).updateCostProfileApiV1AdminWireframeCostProfilesProfileIdPut(
        selectedProfileId!,
        {
          name: draft.name.trim(),
          annual_low_usd: Number(draft.low),
          annual_mid_usd: Number(draft.mid),
          annual_high_usd: Number(draft.high),
        },
        timeframe
      ),
    onSuccess: refresh,
  });
  const archiveMutation = useMutation({
    mutationFn: (profileId: number) =>
      getService(
        AdminWireframeService
      ).archiveCostProfileApiV1AdminWireframeCostProfilesProfileIdArchivePost(profileId, timeframe),
    onSuccess: async () => {
      setSelectedProfileId(null);
      setDraft(emptyDraft);
      await refresh();
    },
  });
  const applyMutation = useMutation({
    mutationFn: () =>
      getService(
        AdminWireframeService
      ).patchCostProfileAssignmentsApiV1AdminWireframeCostProfileAssignmentsPatch(
        {
          changes: assignmentChanges,
        },
        timeframe
      ),
    onSuccess: refresh,
  });
  const headcountMutation = useMutation({
    mutationFn: () =>
      getService(
        AdminWireframeService
      ).patchWireframeHeadcountDataApiV1AdminWireframeHeadcountDataPatch(
        {
          changes: Object.entries(headcountDrafts).map(([key, data]) => ({
            ...fromHeadcountKey(key),
            data: isBlankStaffing(data) ? null : fillBlankStaffingValues(data),
          })),
        },
        timeframe
      ),
    onSuccess: async () => {
      setHeadcountDrafts({});
      await refresh();
    },
  });
  const data = dataQuery.data;
  const profiles = data?.profiles.filter((profile) => !profile.archived_at) ?? [];
  const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId);
  const assignedWorkstreamIds = new Set(
    data?.assignments
      .filter((assignment) => assignment.profile_id === selectedProfileId)
      .map((assignment) => assignment.workstream_id) ?? []
  );
  const assignmentChanges = [
    ...selectedWorkstreams
      .filter((workstreamId) => !assignedWorkstreamIds.has(workstreamId))
      .map((workstream_id) => ({workstream_id, profile_id: selectedProfileId})),
    ...[...assignedWorkstreamIds]
      .filter((workstreamId) => !selectedWorkstreams.includes(workstreamId))
      .map((workstream_id) => ({workstream_id, profile_id: null})),
  ];
  const selectProfile = (profileId: number) => {
    const profile = profiles.find(({id}) => id === profileId);
    setSelectedProfileId(profileId);
    if (profile) setDraft(toDraft(profile));
    setSelectedWorkstreams(
      data?.assignments
        .filter((assignment) => assignment.profile_id === profileId)
        .map((assignment) => assignment.workstream_id) ?? []
    );
  };
  const assignedNames = (profileId: number) => {
    const ids = new Set(
      data?.assignments
        .filter((item) => item.profile_id === profileId)
        .map((item) => item.workstream_id)
    );
    return (
      data?.workstreams.filter((row) => ids.has(row.workstream_id)).map((row) => row.name) ?? []
    );
  };
  const orderedWorkstreams = data
    ? buildWireframeWorkstreamHierarchy(data.workstreams, data.narrative_root_workstream_id)
    : [];
  const filteredWorkstreams = orderedWorkstreams.filter(({workstream}) =>
    workstream.name.toLowerCase().includes(search.toLowerCase())
  );
  const pillars = data
    ? listWireframePillars(data.workstreams, data.narrative_root_workstream_id)
    : [];
  const activePillarId = pillars.some(({workstream_id}) => workstream_id === selectedPillarId)
    ? selectedPillarId
    : (pillars[0]?.workstream_id ?? null);
  const directProfileIds = new Map(
    data?.workstreams.map(({workstream_id, direct_profile_id}) => [
      workstream_id,
      direct_profile_id,
    ]) ?? []
  );
  const tableRows: WireframeTableRow[] = (
    data && activePillarId != null
      ? buildWireframePillarHierarchy(data.workstreams, activePillarId)
      : []
  ).map(({workstream, depth, ancestorIds}) => {
    // Inherit along the path this pillar's table shows the row under.
    const effectiveProfileId =
      workstream.direct_profile_id ??
      [...ancestorIds]
        .reverse()
        .map((ancestorId) => directProfileIds.get(ancestorId))
        .find((profileId) => profileId != null) ??
      null;
    const profile = profiles.find(({id}) => id === effectiveProfileId);
    const pillarId = activePillarId!;
    const headcount =
      workstream.headcount_data.find(
        ({pillar_workstream_id}) => pillar_workstream_id === pillarId
      ) ?? null;
    const estimatedCost =
      headcount && profile
        ? 0.5 *
          (effectiveGeoValue(headcount, 'low') * profile.annual_low_usd +
            effectiveGeoValue(headcount, 'mid') * profile.annual_mid_usd +
            effectiveGeoValue(headcount, 'high') * profile.annual_high_usd)
        : null;
    return {
      workstream,
      pillarId,
      depth,
      headcount,
      profileName: profile?.name ?? null,
      estimatedCost,
    };
  });
  const tableColumns: Column<WireframeTableRow>[] = [
    {type: 'Workstream', minWidth: WORKSTREAM_COLUMN_WIDTH, spanner: true},
    ...(['HC', 'Growth', 'CI', 'KTLO', 'Low', 'Mid', 'High'] as const).map((type) => ({
      type,
      fixed: true,
      minWidth: INPUT_COLUMN_WIDTH,
    })),
    {type: 'Profile', fixed: true, minWidth: PROFILE_COLUMN_WIDTH},
    {type: 'Est. cost', fixed: true, minWidth: ESTIMATED_COST_COLUMN_WIDTH},
    {type: 'Clear', fixed: true, minWidth: CLEAR_COLUMN_WIDTH},
  ];
  const unsavedHintId = useId();
  const errorHintIds = {
    [VALUE_FORMAT_ERROR]: useId(),
    [ALLOCATION_MISMATCH_ERROR]: useId(),
    [HEADCOUNT_TOO_LARGE_ERROR]: useId(),
  };
  // Rows without saved data show blank cells rather than zeros until a value is entered.
  const getSavedHeadcount = (row: WireframeTableRow) =>
    row.headcount ? toStaffingValues(row.headcount) : EMPTY_STAFFING_VALUES;
  const getHeadcountDraft = (row: WireframeTableRow): StaffingValues =>
    headcountDrafts[toHeadcountKey(row.pillarId, row.workstream.workstream_id)] ??
    getSavedHeadcount(row);
  const updateHeadcount = (row: WireframeTableRow, values: Partial<StaffingValues>) => {
    const draftKey = toHeadcountKey(row.pillarId, row.workstream.workstream_id);
    const original = getHeadcountDraft(row);
    const next = {...original, ...values};
    const saved = getSavedHeadcount(row);
    const matchesSaved = (Object.keys(saved) as StaffingField[]).every((key) =>
      isSameStaffingValue(next[key], saved[key])
    );
    setHeadcountDrafts((current) => {
      if (matchesSaved) {
        const remaining = {...current};
        delete remaining[draftKey];
        return remaining;
      }
      return {...current, [draftKey]: next};
    });
  };
  // Fills a block copied from a spreadsheet rightward and downward from the pasted cell, dropping
  // anything that falls outside the table. Single values paste natively.
  const pasteHeadcount = (
    event: ClipboardEvent<HTMLInputElement>,
    row: WireframeTableRow,
    field: StaffingField
  ) => {
    const text = event.clipboardData.getData('text/plain');
    if (!isMultiCellClipboard(text)) return;
    event.preventDefault();
    const startRow = tableRows.indexOf(row);
    const startColumn = EDITABLE_COLUMNS.findIndex((column) => column.field === field);
    parseSpreadsheetClipboard(text).forEach((cells, rowOffset) => {
      const target = tableRows[startRow + rowOffset];
      if (!target) return;
      const values: Partial<StaffingValues> = {};
      cells.forEach((cell, columnOffset) => {
        const column = EDITABLE_COLUMNS[startColumn + columnOffset];
        if (column) values[column.field] = normalizePastedNumber(cell);
      });
      updateHeadcount(target, values);
    });
  };
  const getCellError = (validation: StaffingValidation, field: StaffingField) => {
    if (validation.invalidFields.has(field)) return VALUE_FORMAT_ERROR;
    if (validation.allocationMismatch && (ALLOCATION_FIELDS as readonly string[]).includes(field))
      return ALLOCATION_MISMATCH_ERROR;
    if (validation.headcountTooLarge && (GEO_FIELDS as readonly string[]).includes(field))
      return HEADCOUNT_TOO_LARGE_ERROR;
    return null;
  };
  const renderNumberInput = (row: WireframeTableRow, field: StaffingField, label: string) => {
    const rowDraft = getHeadcountDraft(row);
    const unsaved = !isSameStaffingValue(rowDraft[field], getSavedHeadcount(row)[field]);
    const validation = validateStaffing(fillBlankStaffingValues(rowDraft));
    const error = getCellError(validation, field);
    const tooltipMessage =
      error === ALLOCATION_MISMATCH_ERROR && validation.allocationCents != null
        ? `${error} (current sum: ${formatStaffingCents(validation.allocationCents)})`
        : error;
    const describedBy = [unsaved && unsavedHintId, error && errorHintIds[error]]
      .filter(Boolean)
      .join(' ');
    // Always render the tooltip wrapper so toggling the error never remounts a focused input.
    return (
      <Tooltip title={tooltipMessage ?? ''} placement="top" isDisabled={!error}>
        <span className={styles.tableCellTooltipTrigger}>
          <TextInput
            size="small"
            type="number"
            min={0}
            step={0.01}
            inputMode="decimal"
            wrapperProps={{
              className: classNames(styles.tableCellInput, {
                [styles.unsaved]: unsaved,
                [styles.invalid]: error,
              }),
            }}
            aria-label={`${row.workstream.name} ${label}`}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : undefined}
            value={rowDraft[field]}
            onChange={(event) => updateHeadcount(row, {[field]: event.currentTarget.value})}
            onPaste={(event) => pasteHeadcount(event, row, field)}
          />
        </span>
      </Tooltip>
    );
  };
  const renderHeadcount = (row: WireframeTableRow) => {
    const rowDraft = getHeadcountDraft(row);
    const {headcountCents} = validateStaffing(fillBlankStaffingValues(rowDraft));
    const isUnset = GEO_FIELDS.every((field) => isBlankStaffingValue(rowDraft[field]));
    return (
      <Text
        tagName="output"
        size="small"
        className={styles.tableCellValue}
        aria-label={`${row.workstream.name} headcount`}
      >
        {isUnset ? '' : headcountCents == null ? '—' : formatStaffingCents(headcountCents)}
      </Text>
    );
  };
  // Blanks every input; once saved, the row's stored headcount is deleted.
  const renderClearHeadcount = (row: WireframeTableRow) => {
    if (isBlankStaffing(getHeadcountDraft(row))) return null;
    const label = `Clear ${row.workstream.name} headcount`;
    return (
      <Tooltip title="Clear headcount" placement="top">
        <IconButton
          variant="borderless"
          size="small"
          aria-label={label}
          onClick={() => updateHeadcount(row, EMPTY_STAFFING_VALUES)}
        >
          <UIIcon src={DeleteLine} size="small" />
        </IconButton>
      </Tooltip>
    );
  };
  const hasHeadcountChanges = Object.keys(headcountDrafts).length > 0;
  const hasInvalidHeadcount = Object.values(headcountDrafts).some(
    (rowDraft) => !validateStaffing(fillBlankStaffingValues(rowDraft)).isValid
  );
  const updateDraft = (key: keyof Draft, value: string, isNew = false) =>
    (isNew ? setNewDraft : setDraft)((current) => ({...current, [key]: value}));
  const valid = (value: Draft) =>
    value.name.trim() && [value.low, value.mid, value.high].every((rate) => /^\d+$/.test(rate));

  const renderFields = (value: Draft, setter: (key: keyof Draft, value: string) => void) => (
    <div className={styles.fields}>
      <TextInput
        aria-label="Profile name"
        placeholder="New profile name"
        value={value.name}
        onChange={(event) => setter('name', event.currentTarget.value)}
      />
      {(['low', 'mid', 'high'] as const).map((geo) => (
        <TextInput
          key={geo}
          aria-label={`${geo} annual cost per HC`}
          type="number"
          placeholder={`${geo[0].toUpperCase()}${geo.slice(1)} $`}
          value={value[geo]}
          onChange={(event) => setter(geo, event.currentTarget.value)}
        />
      ))}
    </div>
  );

  return (
    <Layout.Container width="100%">
      <main className={styles.page}>
        <span id={unsavedHintId} hidden>
          Unsaved change
        </span>
        {Object.entries(errorHintIds).map(([message, id]) => (
          <span key={id} id={id} hidden>
            {message}
          </span>
        ))}
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Text tagName="span" size="small" color="subtle">
            Admin
          </Text>
          <Text tagName="span" size="small" color="subtle">
            /
          </Text>
          <Text tagName="span" size="small">
            Wireframe Data
          </Text>
        </nav>
        <header className={styles.pageHeader}>
          <Title>Wireframe Data</Title>
          <Select
            id="planning-period"
            aria-label="Planning period"
            value={timeframe}
            onChange={(value) => {
              setPickedTimeframe(value);
              setSelectedProfileId(null);
              setSelectedWorkstreams([]);
            }}
          >
            {timeframes.map(({name}) => (
              <Select.Option key={name} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </header>
        <section className={styles.panel} aria-labelledby="cost-profiles-heading">
          <div className={styles.panelHeader}>
            <Text id="cost-profiles-heading" variant="label" size="large" isBold>
              Cost input profiles
            </Text>
            <Text tagName="p" color="subtle" size="small">
              Annual cost per HC for Low, Mid and High geos. Select a profile to edit it or apply
              it.
            </Text>
            <form
              className={styles.addRow}
              onSubmit={(event) => {
                event.preventDefault();
                createMutation.mutate();
              }}
            >
              {renderFields(newDraft, (key, value) => updateDraft(key, value, true))}
              <Button
                variant="primary"
                type="submit"
                disabled={!valid(newDraft) || createMutation.isPending}
              >
                ＋ Add
              </Button>
            </form>
          </div>
          {isLoadingData ? (
            <Text className={styles.status} color="subtle">
              Loading cost profiles…
            </Text>
          ) : null}
          {dataQuery.error ? (
            <SystemBanner tone="alert">
              <Text>Unable to load profiles. Your Finance access may be required.</Text>
            </SystemBanner>
          ) : null}
          {profiles.map((profile) => {
            const active = profile.id === selectedProfileId;
            const savedDraft = toDraft(profile);
            const isUnsaved = (key: keyof Draft) => active && draft[key] !== savedDraft[key];
            const hasUnsavedChanges = (Object.keys(savedDraft) as (keyof Draft)[]).some(isUnsaved);
            const names = assignedNames(profile.id);
            return (
              <div className={styles.profile} key={profile.id}>
                <div className={styles.profileRow}>
                  <RadioButton
                    name="selected-profile"
                    aria-label={`Select ${profile.name}`}
                    checked={active}
                    onChange={() => selectProfile(profile.id)}
                  />
                  <TextInput
                    className={styles.profileName}
                    wrapperProps={{className: classNames({[styles.unsaved]: isUnsaved('name')})}}
                    aria-label={`${profile.name} profile name`}
                    aria-describedby={isUnsaved('name') ? unsavedHintId : undefined}
                    value={active ? draft.name : profile.name}
                    onFocus={() => {
                      if (!active) selectProfile(profile.id);
                    }}
                    onChange={(event) => updateDraft('name', event.currentTarget.value)}
                  />
                  {(['low', 'mid', 'high'] as const).map((geo) => (
                    <label className={styles.rate} key={geo}>
                      <span>{geo}</span>
                      <TextInput
                        type="number"
                        wrapperProps={{className: classNames({[styles.unsaved]: isUnsaved(geo)})}}
                        aria-label={`${profile.name} ${geo} rate`}
                        aria-describedby={isUnsaved(geo) ? unsavedHintId : undefined}
                        value={
                          active
                            ? draft[geo]
                            : String(
                                profile[
                                  `annual_${geo}_usd` as
                                    | 'annual_low_usd'
                                    | 'annual_mid_usd'
                                    | 'annual_high_usd'
                                ]
                              )
                        }
                        onFocus={() => {
                          if (!active) selectProfile(profile.id);
                        }}
                        onChange={(event) => updateDraft(geo, event.currentTarget.value)}
                      />
                    </label>
                  ))}
                  <IconButton
                    variant="borderless"
                    size="medium"
                    aria-label={`Archive ${profile.name}`}
                    onClick={() => archiveMutation.mutate(profile.id)}
                  >
                    <UIIcon src={DeleteLine} size="medium" />
                  </IconButton>
                </div>
                <div className={styles.assignments}>
                  {names.length ? (
                    names.map((name) => (
                      <Text tagName="span" size="small" className={styles.chip} key={name}>
                        ▥ {name}
                      </Text>
                    ))
                  ) : (
                    <Text tagName="span" size="small" color="subtle" className={styles.muted}>
                      Not applied yet
                    </Text>
                  )}
                </div>
                {hasUnsavedChanges && (
                  <div className={styles.editActions}>
                    <Button
                      variant="primary"
                      onClick={() => saveMutation.mutate()}
                      disabled={!valid(draft) || saveMutation.isPending}
                    >
                      Save profile
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {selectedProfile && (
            <div className={styles.applyRow}>
              <Text tagName="span">
                Apply “{selectedProfile?.name ?? 'profile'}” for {timeframe} to
              </Text>
              <Popover
                open={workstreamPickerOpen}
                onToggle={setWorkstreamPickerOpen}
                placement="bottom-start"
                offsetDistance={4}
                isPortaled={false}
              >
                <Popover.Trigger asChild>
                  <Button
                    variant="borderless"
                    className={styles.workstreamTrigger}
                    aria-label="Select workstreams"
                  >
                    <Box className={styles.workstreamTriggerContent}>
                      <Text size="small" color="subtle">
                        {selectedWorkstreams.length
                          ? `${selectedWorkstreams.length} workstreams selected`
                          : 'Select workstreams…'}
                      </Text>
                      <UIIcon src={ChevronDownLine} size="small" />
                    </Box>
                  </Button>
                </Popover.Trigger>
                <Popover.Overlay>
                  <Popover.Content className={styles.pickerMenu}>
                    <Popover.Body>
                      <Stack gap="8">
                        <TextInput
                          aria-label="Search workstreams"
                          placeholder="Search workstreams…"
                          value={search}
                          onChange={(event) => setSearch(event.currentTarget.value)}
                        />
                        <div className={styles.pickerList}>
                          {filteredWorkstreams.map(({workstream, depth}) => (
                            <label
                              key={workstream.workstream_id}
                              style={{paddingLeft: 4 + depth * 16}}
                            >
                              <Checkbox
                                checked={selectedWorkstreams.includes(workstream.workstream_id)}
                                onChange={(event) =>
                                  setSelectedWorkstreams((current) =>
                                    event.currentTarget.checked
                                      ? [...current, workstream.workstream_id]
                                      : current.filter((id) => id !== workstream.workstream_id)
                                  )
                                }
                              />
                              <Text size="small">{workstream.name}</Text>
                            </label>
                          ))}
                        </div>
                      </Stack>
                    </Popover.Body>
                  </Popover.Content>
                </Popover.Overlay>
              </Popover>
              <Button
                variant="opacity"
                onClick={() => applyMutation.mutate()}
                disabled={
                  !selectedProfile || assignmentChanges.length === 0 || applyMutation.isPending
                }
              >
                Apply
              </Button>
            </div>
          )}
          {(createMutation.error ||
            saveMutation.error ||
            archiveMutation.error ||
            applyMutation.error) && (
            <SystemBanner tone="alert">
              <Text>Could not save this change. Check the values and try again.</Text>
            </SystemBanner>
          )}
        </section>
        <section className={styles.workstreamsSection} aria-labelledby="workstreams-heading">
          <div className={styles.workstreamsHeader}>
            <Text id="workstreams-heading" variant="label" size="large" isBold>
              Workstreams
            </Text>
          </div>
          {pillars.length > 0 && activePillarId != null ? (
            <Tabs
              selectedTab={String(activePillarId)}
              onSelection={(tabId) => setSelectedPillarId(Number(tabId))}
            >
              <Tabs.Group hasHorizontalPadding={false}>
                {pillars.map((pillar) => (
                  <Tabs.Tab key={pillar.workstream_id} id={String(pillar.workstream_id)}>
                    {pillar.name}
                  </Tabs.Tab>
                ))}
              </Tabs.Group>
              <div className={styles.tabsDivider} />
              <Tabs.Panel tabId={String(activePillarId)}>
                {hasHeadcountChanges && (
                  <div className={styles.tableActions}>
                    <Button
                      variant="primary"
                      onClick={() => headcountMutation.mutate()}
                      disabled={headcountMutation.isPending || hasInvalidHeadcount}
                      isLoading={headcountMutation.isPending}
                    >
                      Save
                    </Button>
                  </div>
                )}
                <Table
                  columns={tableColumns}
                  data={tableRows}
                  compact
                  columnSizing="share"
                  resizable={false}
                  className={styles.workstreamsTable}
                  scrollAreaLabel="Workstreams cost and headcount data"
                >
                  <Table.Header />
                  <Table.Body>
                    {tableRows.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={tableColumns.length}>
                          <Text color="subtle">No workstreams in this pillar.</Text>
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      <Table.Rows
                        renderRow={(row) => (
                          <Table.Row key={row.workstream.workstream_id}>
                            <Table.Cell>
                              <Box style={{paddingLeft: Math.min(row.depth, 5) * 16}}>
                                <Text>{row.workstream.name}</Text>
                              </Box>
                            </Table.Cell>
                            <Table.Cell style={{padding: 0}}>{renderHeadcount(row)}</Table.Cell>
                            {EDITABLE_COLUMNS.map(({field, label}) => (
                              <Table.Cell key={field} style={{padding: 0}}>
                                {renderNumberInput(row, field, label)}
                              </Table.Cell>
                            ))}
                            <Table.Cell>
                              <Text color={row.profileName ? undefined : 'subtle'}>
                                {row.profileName ?? '—'}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Text color={row.estimatedCost == null ? 'subtle' : undefined}>
                                {row.estimatedCost == null
                                  ? '—'
                                  : row.estimatedCost.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      maximumFractionDigits: 0,
                                    })}
                              </Text>
                            </Table.Cell>
                            <Table.Cell style={{padding: 0}}>
                              <div className={styles.tableCellAction}>
                                {renderClearHeadcount(row)}
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      />
                    )}
                  </Table.Body>
                </Table>
              </Tabs.Panel>
            </Tabs>
          ) : isLoadingData ? (
            <Text color="subtle">Loading workstreams…</Text>
          ) : (
            <Text color="subtle">No pillar workstreams found for this planning period.</Text>
          )}
          {headcountMutation.error && (
            <SystemBanner tone="alert">
              <Text>
                Unable to save headcount changes. Check that each value has up to 2 decimal places
                and that Growth, CI, and KTLO add up to HC.
              </Text>
            </SystemBanner>
          )}
        </section>
      </main>
    </Layout.Container>
  );
};
